import NavComponent from "../lib/nav_component";
import { PaneLinkComponent } from "./pane_link_component";
import { PaneInfoComponent } from "./pane_info_component";
import { PanePreComponent } from "./pane_pre_component";
import { canCountNavImpression } from "../lib/drawer_gated_impressions";
import { ExposureObserver, ExposureRules } from "../../../helpers/exposure_observer";

export const ExperiencePaneOptionComponentConfig = {
    componentTypeKey: 'ExperiencePaneOptionComponent',
    baseHtmlClass: 'nav_option',
    selector: '.nav_option--experience, .nav_option--itinerary_item',

    datasetConfigKey: 'nav_option__online_app_nav_data',
    identifierDatasetConfigKey: 'identifier',
    statsIdentifierDatasetConfigKey: 'stats_identifier',
    analyticsParametersDatasetConfigKey: 'analytics_parameters',
    preventDefaultActionDatasetConfigKey: 'prevent_default_action',
}

export class ExperiencePaneOptionComponent extends NavComponent {

    constructor(id_or_element, secondaryPane, secondaryPanesManager) {
        super(id_or_element, ExperiencePaneOptionComponentConfig);

        this.setDatasetConfig(ExperiencePaneOptionComponentConfig.datasetConfigKey);
        this.identifier = this.getIdentifier();
        this.statsIdentifier = this.getStatsIdentifier();
        this.analyticsParameters = this.getAnalyticsParameters();
        this.preventDefaultAction = this.getPreventDefaultActionDatasetConfig();

        this.secondaryPane = secondaryPane;
        this.secondaryPanesManager = secondaryPanesManager;

        // Set up click handling via subcomponents (consistent with PaneOptionComponent)
        this.paneLinkComponent = PaneLinkComponent.new(this.element, this);
        this.paneInfoComponent = PaneInfoComponent.new(this.element, this);
        this.panePreComponent = PanePreComponent.new(this.element, this);

        // Set up observer to handle capturing stats impressions (views) of the component within it's parent element.
        this.viewedObserver = null;
        this.initViewedObserver();
        // The SecondaryPane observes its options after it gains focus and stops before it blurs
        // (mirroring PrimaryPane), so only observe now if the pane is already focused.
        if (this.secondaryPane && this.secondaryPane.isFocused()) this.observeViewed();
    }

    getIdentifier() {
        const identifier = this.getDatasetConfigValue(ExperiencePaneOptionComponentConfig.identifierDatasetConfigKey);
        if (!identifier) return;
        return identifier;
    }

    getStatsIdentifier() {
        return this.getDatasetConfigValue(ExperiencePaneOptionComponentConfig.statsIdentifierDatasetConfigKey);
    }

    getAnalyticsParameters() {
        return this.getDatasetConfigValue(ExperiencePaneOptionComponentConfig.analyticsParametersDatasetConfigKey);
    }

    getPreventDefaultActionDatasetConfig() {
        const preventDefaultAction = this.getDatasetConfigValue(ExperiencePaneOptionComponentConfig.preventDefaultActionDatasetConfigKey);
        if (!preventDefaultAction) return;
        return preventDefaultAction;
    }

    // First check if the nav is in a native app (where we never prevent default actions on the navigation options).
    // Native apps never prevent default actions because the native code relies on these actions (and their URL) for routing.
    // Otherwise fall back to the configuration on the element.
    shouldPreventDefaultAction() {
        if (this.getNav()?.isNativeApp) {
            console.log('[ExperiencePaneOptionComponent] Not preventing default - native app context');
            return false;
        }
        return !!this.preventDefaultAction;
    }

    // Stub methods required by Nav state machine actions (via PaneLinkComponent/PaneInfoComponent).
    // These are no-ops for experience/itinerary options which don't have associated panes.
    getNavToggleInstruction() { return null; }
    shouldOpenPane() { return false; }
    recordShowingContent() { }
    getAssociatedPane() { return this.secondaryPane; }
    hasAssociatedPane() { return !!this.secondaryPane; }
    trackOpened() { }
    isNavPaneShowingContent() { return true; }

    /**
     * Returns the Nav object associated with the secondaryPanesManager
     *
     * @return {Nav|undefined} The Nav if present.
     */
    getNav() {
        if (!this.secondaryPanesManager) return;
        return this.secondaryPanesManager.nav;
    }

    /**
     * Returns the component's parent element that is used for detecting if the component is visible.
     *
     * @return {Node|undefined} The DOM element that the component's element scrolls within.
     */
    getParentScrollElement() {
        if (!this.secondaryPane || !this.secondaryPane.paneComponent) return;
        return this.secondaryPane.paneComponent.getScrollingBodyElement();
    }

    /**
     * Tell the viewed intersection observer to start observing.
     * @return {undefined}
     */
    observeViewed() {
        if (!this.viewedObserver) return;
        this.viewedObserver.start();
    }

    /**
     * Tell the viewed intersection observer to stop observing.
     * @return {undefined}
     */
    unobserveViewed() {
        if (!this.viewedObserver) return;
        this.viewedObserver.stop();
    }

    /**
     * Count an impression withheld while the nav was shut, if this option is still in view.
     * Called by the Nav when it opens (`Nav#flushPendingImpressions`).
     * @return {undefined}
     */
    flushPendingViewed() {
        if (!this.viewedObserver) return;
        this.viewedObserver.flush();
    }

    /**
     * Set up the observer that counts one impression per time this option is seen in its pane.
     * A deal card can be taller than the pane, so "seen" is fully visible OR filling the pane; and
     * it counts only while the nav is open and the pane focused (`canCountNavImpression`).
     * See app/javascript/rankers/helpers/exposure_observer.js.
     * @return {undefined}
     */
    initViewedObserver() {
        if (typeof IntersectionObserver === 'undefined') return;
        this.viewedObserver = new ExposureObserver(this.element, {
            root: this.getParentScrollElement(),
            rootMargin: '10px',
            rule: ExposureRules.fullyVisibleOrFillsRoot,
            onExposure: () => this.trackViewed(),
            canCount: () => canCountNavImpression(this.getNav(), this.secondaryPane),
        });
        this.onDestroy(() => this.viewedObserver.destroy());
    }

    render() {
    }

    trackViewed() {
        if (!this.statsIdentifier) return;

        const eventName = window.RankersAnalytics?.EventName?.EXPERIENCE_LISTING_VIEWED
        if (!eventName) return;

        let nav = this.getNav();
        if (!nav) return;

        // We include the `eventLabel` option for backwards compatibility with older
        // native apps which only handled `eventAction` and `eventLabel` params (the `eventAction` comes from the `eventName`).
        if (nav.rankersAnalyticsEventTracker) {
            const backwardsCompatibilityOptions = {eventLabel: this.statsIdentifier}; // See tracker.js `getBackwardsCompatibilityParams()` function to understand this!
            const options = this.analyticsParameters ? this.analyticsParameters : {experience_id: this.statsIdentifier, item_id: this.statsIdentifier};
            const allOptions = {...backwardsCompatibilityOptions, ...options};
            nav.rankersAnalyticsEventTracker(eventName, allOptions);
        }
    }

}